// Like p-limit, but each task carries a "weight" (e.g. estimated bytes of memory it needs) and
// admission is gated by a total weight budget rather than a flat task count. A task is always
// admitted immediately when nothing else is running, even if its own weight exceeds the whole
// budget - otherwise a single oversized task would deadlock the queue forever.
export class WeightedLimit {
    constructor(private readonly maxWeight: number) {}

    async run<T>(weight: number, fn: () => Promise<T>): Promise<T> {
        await this.acquire(weight)
        try {
            return await fn()
        } finally {
            this.release(weight)
        }
    }

    private acquire(weight: number): Promise<void> {
        return new Promise((resolve) => {
            const tryAcquire = () => {
                const fits = this.currentWeight + weight <= this.maxWeight || this.currentCount === 0
                if (fits) {
                    this.currentWeight += weight
                    this.currentCount += 1
                    resolve()
                } else {
                    this.queue.push(tryAcquire)
                }
            }
            tryAcquire()
        })
    }

    private release(weight: number) {
        this.currentWeight -= weight
        this.currentCount -= 1
        this.queue.shift()?.()
    }

    private currentWeight = 0
    private currentCount = 0
    private queue: (() => void)[] = []
}
